import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { fmtDate } from '../lib/utils'

function money(value) {
  return `GBP ${(Number(value) || 0).toFixed(2)}`
}

function componentLabel(component = {}) {
  return [component.year, component.brand, component.model].filter(Boolean).join(' ') || 'Suspension component'
}

export default function QuoteApprovalPage() {
  const { token } = useParams()
  const [quote, setQuote] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState('')
  const [notice, setNotice] = useState('')

  useEffect(() => {
    let cancelled = false
    async function loadQuote() {
      setLoading(true)
      setError('')
      try {
        const res = await fetch('/.netlify/functions/quote-response?token=' + encodeURIComponent(token))
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.error || 'Quote link could not be loaded.')
        if (!cancelled) setQuote(data.quote)
      } catch (err) {
        if (!cancelled) setError(err.message || 'Quote link could not be loaded.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    loadQuote()
    return () => { cancelled = true }
  }, [token])

  async function respond(decision) {
    setSubmitting(decision)
    setError('')
    setNotice('')
    try {
      const res = await fetch('/.netlify/functions/quote-response?token=' + encodeURIComponent(token), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision, note }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Could not save your response.')
      setQuote(data.quote)
      setNotice(decision === 'approved'
        ? 'Thanks, your approval has been recorded for the workshop.'
        : 'Thanks, your response has been recorded for the workshop.')
    } catch (err) {
      setError(err.message || 'Could not save your response.')
    } finally {
      setSubmitting('')
    }
  }

  const alreadyResponded = quote?.customer_decision === 'approved' || quote?.customer_decision === 'declined' || quote?.can_respond === false

  return (
    <div style={{ minHeight: '100vh', background: '#0f0f0f', color: '#fff', padding: '32px 16px' }}>
      <div style={{ maxWidth: 720, margin: '0 auto', background: '#1a1a1a', border: '1px solid #303030', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ padding: '22px 26px', borderBottom: '3px solid #e85d04', background: '#111' }}>
          {quote?.workshop?.logo_url ? (
            <img src={quote.workshop.logo_url} alt={quote.workshop.name} style={{ maxWidth: 220, maxHeight: 64, objectFit: 'contain', background: '#fff' }} />
          ) : (
            <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase' }}>
              {quote?.workshop?.name || 'Rebound'} <span style={{ color: '#e85d04' }}>Quote</span>
            </div>
          )}
        </div>

        <div style={{ padding: 26 }}>
          {loading && <div style={{ color: '#aaa' }}>Loading quote...</div>}
          {error && <div style={{ background: '#331b1b', border: '1px solid #c0392b', color: '#ffb6aa', padding: 12, borderRadius: 6, marginBottom: 16 }}>{error}</div>}
          {notice && <div style={{ background: '#173525', border: '1px solid #2d9e6b', color: '#8ff0bd', padding: 12, borderRadius: 6, marginBottom: 16 }}>{notice}</div>}

          {quote && (
            <>
              <h1 style={{ margin: '0 0 6px', fontSize: 26 }}>Service Quote</h1>
              <div style={{ color: '#aaa', marginBottom: 22 }}>{quote.workshop.name} - {fmtDate(quote.date)}</div>

              <div style={{ display: 'grid', gap: 10, marginBottom: 18 }}>
                <Info label="Customer" value={quote.customer_name} />
                {quote.module_key === 'general' ? <Info label="Work type" value="General Workshop service" /> : <>
                  <Info label="Component" value={componentLabel(quote.component)} />
                  {quote.component.serial && <Info label="Serial" value={quote.component.serial} mono />}
                </>}
                {quote.service_summary && <Info label="Selected Service" value={quote.service_summary} multiline />}
                {quote.inspection_summary && <Info label="Inspection Summary" value={quote.inspection_summary} multiline />}
                {quote.estimate_notes && <Info label="Quote Notes" value={quote.estimate_notes} multiline />}
                {quote.further_work && <Info label="Further Work" value={quote.further_work} multiline />}
                {quote.worn_parts && <Info label="Worn Parts" value={quote.worn_parts} multiline />}
                {quote.vat ? <>
                  <Info label="Subtotal" value={money(quote.vat.tax.net_minor / 100)} />
                  <Info label={quote.vat.registration === 'registered' ? 'VAT' : 'VAT not charged — not VAT registered'} value={money(quote.vat.tax.tax_minor / 100)} />
                </> : <Info label="Service Cost" value={money(quote.service_cost)} />}
                {Number(quote.quote_amount || 0) > 0 && <Info label="Additional Quote" value={money(quote.quote_amount)} />}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center', background: '#111', borderLeft: '3px solid #e85d04', padding: '16px 18px', marginBottom: 22 }}>
                <span style={{ color: '#aaa', fontSize: 13, textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700 }}>Total if approved</span>
                <strong style={{ fontSize: 24 }}>{money(quote.estimate_amount)}</strong>
              </div>

              {alreadyResponded ? (
                <div style={{ color: quote.customer_decision === 'approved' ? '#2d9e6b' : '#ff6b57', fontWeight: 700, textTransform: 'capitalize' }}>
                  Quote {quote.customer_decision}
                </div>
              ) : (
                <>
                  <label style={{ display: 'block', color: '#aaa', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 8 }}>Message to workshop</label>
                  <textarea
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    placeholder="Optional note, question, or approval condition..."
                    style={{ width: '100%', minHeight: 90, borderRadius: 6, border: '1px solid #303030', background: '#222', color: '#fff', padding: 12, boxSizing: 'border-box', marginBottom: 14 }}
                  />
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button onClick={() => respond('approved')} disabled={!!submitting} style={buttonStyle('#2d9e6b')}>
                      {submitting === 'approved' ? 'Sending...' : 'Approve Quote'}
                    </button>
                    <button onClick={() => respond('declined')} disabled={!!submitting} style={buttonStyle('#c0392b')}>
                      {submitting === 'declined' ? 'Sending...' : 'Decline'}
                    </button>
                  </div>
                </>
              )}

              <div style={{ marginTop: 26, borderTop: '1px solid #303030', paddingTop: 14, color: '#888', fontSize: 12 }}>
                Questions? Reply to the original email or contact {quote.workshop.email || quote.workshop.name}.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function Info({ label, value, multiline = false, mono = false }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '170px 1fr', gap: 12, paddingBottom: 10, borderBottom: '1px solid #303030' }}>
      <div style={{ color: '#888', fontSize: 13 }}>{label}</div>
      <div style={{ color: '#fff', whiteSpace: multiline ? 'pre-line' : 'normal', fontFamily: mono ? 'monospace' : 'inherit' }}>{value}</div>
    </div>
  )
}

function buttonStyle(background) {
  return {
    border: 0,
    background,
    color: '#fff',
    borderRadius: 6,
    padding: '12px 18px',
    fontWeight: 800,
    cursor: 'pointer',
  }
}
